import { useState, useCallback, useEffect } from 'react';

type UseSmsFormProps = {
    initialName?: string;
    initialContent?: string;
};

const useSmsForm = ({ initialName = 'Untitled', initialContent = '' }: UseSmsFormProps = {}) => {
    // Form state
    const [name, setName] = useState(initialName);
    const [content, setContent] = useState(initialContent);
    const [isNameTouched, setIsNameTouched] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // Update form when initialValues change
    useEffect(() => {
        if (initialName && initialName !== 'Untitled') {
            setName(initialName);
        }
        if (initialContent) {
            setContent(initialContent);
        }
    }, [initialName, initialContent]);

    // Validation
    const isNameUndefinedOnTouch = !name && isNameTouched;
    const isNameTooShort = isNameTouched && name.length < 3;

    // Helper messages
    const nameHelperText = isNameUndefinedOnTouch
        ? 'Please enter an SMS name'
        : isNameTooShort
        ? 'SMS name must have at least 3 characters'
        : '';

    // Input handlers
    const handleChangeName = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newName = e.target.value;
            setName(newName);
            if (!isNameTouched) setIsNameTouched(true);
        },
        [isNameTouched]
    );

    // Reset form
    const resetForm = useCallback(() => {
        setName(initialName);
        setContent(initialContent);
        setIsNameTouched(false);
        setErrorMessage('');
    }, [initialName, initialContent]);

    return {
        name,
        content,
        isNameTouched,
        isNameUndefinedOnTouch,
        isNameTooShort,
        nameHelperText,
        errorMessage,
        setName,
        setContent,
        setErrorMessage,
        handleChangeName,
        resetForm,
    };
};

export default useSmsForm;
